/** PreventivePanel — publish preventive tips & Founder videos. Extracted from panels.jsx. */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

// Turn a plain YouTube watch/short URL into the /embed/ equivalent for iframes.
const normalizeVideoUrl = (raw) => {
  if (!raw) return "";
  const s = raw.trim();
  const m = s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/))([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return s; // vimeo / already-embed / other providers pass through
};

export function PreventivePanel({ tips, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", season: "all", image_url: "", video_url: "", is_founder: false });

  const save = async () => {
    try {
      await http.post("/preventive", { ...form, video_url: normalizeVideoUrl(form.video_url) });
      toast.success(form.is_founder ? "Founder video published" : "Tip published");
      setOpen(false);
      setForm({ title: "", body: "", season: "all", image_url: "", video_url: "", is_founder: false });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await http.delete(`/preventive/${id}`); reload(); };

  const founders = tips.filter((t) => t.is_founder);
  const others = tips.filter((t) => !t.is_founder);
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-semibold text-lg">Preventive Healthcare content <span className="text-xs text-slate-400 font-normal">· {founders.length} founder video{founders.length === 1 ? "" : "s"} · {others.length} tip{others.length === 1 ? "" : "s"}</span></h2>
        <Button data-testid="add-tip-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> New tip / video</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-tips-grid">
        {tips.map((t) => (
          <Card key={t.id} data-testid={`admin-tip-${t.id}`} className={t.is_founder ? "border-amber-300 bg-amber-50/30" : ""}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {t.is_founder && <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px] uppercase tracking-widest">Founder</Badge>}
                    <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{t.season}</Badge>
                    {t.video_url && <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200 uppercase tracking-widest">Video</Badge>}
                  </div>
                  <CardTitle className="font-heading mt-2 text-base">{t.title}</CardTitle>
                </div>
                <Button variant="ghost" size="sm" onClick={() => del(t.id)} data-testid={`delete-tip-${t.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
              </div>
            </CardHeader>
            <CardContent><p className="text-sm text-slate-600 line-clamp-3">{t.body}</p></CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tip-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>New preventive tip / founder video</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50/40">
              <Switch
                checked={form.is_founder}
                onCheckedChange={(v) => setForm({ ...form, is_founder: v })}
                id="founder-toggle"
                data-testid="tip-founder-toggle"
              />
              <Label htmlFor="founder-toggle" className="text-xs uppercase tracking-widest text-amber-800 font-bold cursor-pointer">Founder video (pinned strip on Parent&apos;s Preventive tab)</Label>
            </div>
            <div><Label>Title</Label><Input data-testid="tip-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
            <div><Label>Season</Label>
              <select data-testid="tip-season-select" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 bg-white text-sm">
                <option>all</option><option>summer</option><option>monsoon</option><option>winter</option>
              </select>
            </div>
            <div>
              <Label>YouTube URL <span className="text-slate-400 text-[11px]">(auto-converts watch/shorts links to /embed/)</span></Label>
              <Input data-testid="tip-video-input" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtu.be/…  or  https://www.youtube.com/watch?v=…" className="mt-1" />
            </div>
            <div><Label>Image URL <span className="text-slate-400 text-[11px]">(shown as banner if no video)</span></Label><Input data-testid="tip-image-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1" /></div>
            <div><Label>Body</Label><Textarea data-testid="tip-body-input" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button data-testid="tip-save-btn" onClick={save}>Publish</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
